import h from 'virtual-dom/h';
import {DefaultSettings} from "../../../../settings/settings-util";
import {AlmeState} from "../../../../wireup/AlmeState";
import {Store} from "redux";
import {VChild, VTree} from "virtual-dom";
import {HistoryMessage, HistoryMessageAlme} from "../../../../types/history";
import {Dictionary, Mutator} from "../../../../types/rendering";


type ChildRenderer = (message: HistoryMessage) => VChild;


export default class Renderer
{

    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _mutators: Mutator[] = [];
    _preRenderers: ChildRenderer[] = [];
    _messageRenderers: ChildRenderer[] = [];
    _postRenderers: ChildRenderer[] = [];


    //#region Public Interface

    constructor(settings: DefaultSettings)
    {
        this._settings = settings;
    }

    initialize(store: Store<AlmeState>,
               mutators: Mutator[],
               preRenderers: ChildRenderer[],
               messageRenderers: ChildRenderer[],
               postRenderers: ChildRenderer[]): void
    {
        this._store = store;
        this._mutators = mutators;
        this._preRenderers = preRenderers;
        this._messageRenderers = messageRenderers;
        this._postRenderers = postRenderers;
    }

    doesRender(message: HistoryMessage): boolean
    {
        const historyMessage = message as HistoryMessageAlme;
        return (historyMessage.type === this._settings.messageConstants.almeResponseType && historyMessage.text && historyMessage.text.length > 0) ||
        historyMessage.type === this._settings.messageConstants.errorResponseType;
    }

    getRender(): ChildRenderer
    {
        return (message): VChild =>
        {
            if (!this.doesRender(message)) return [];

            const almeMessage = message as HistoryMessageAlme;
            const { almeResponsePersonaIconClassName, almeResponseTextClassName } = this._settings.classNames;

            const preNodes = this.renderNodes(this._preRenderers, almeMessage);
            const messageChildNodes = this.renderNodes(this._messageRenderers, almeMessage);
            const postNodes = this.renderNodes(this._postRenderers, almeMessage);

            const { postMarkdownVdomElements } = almeMessage.extensionData;

            let textChildren = (postMarkdownVdomElements as VTree[])?.length > 0 ? (postMarkdownVdomElements as VTree[]) : almeMessage.text;
            if (message.type === this._settings.messageConstants.errorResponseType) { textChildren = ""; }
            const textChildrenSpan = h('span', {}, [textChildren]);

            const personaNode = h('div', { className: almeResponsePersonaIconClassName }, []);
            const textNode = h('div', { className: almeResponseTextClassName }, [textChildrenSpan]);
            const messageNode = h('div', this.buildMessageProperties(), [textNode, ...messageChildNodes]);
            const styleNode = h('div', { style: { clear: 'both' } }, []);

            const messageNodes = [personaNode, messageNode, styleNode];

            return h('div', this.buildResponseProperties(almeMessage), [...preNodes, ...messageNodes, ...postNodes]);
        };
    }

    //#endregion



    //#region Helper Methods

    renderNodes(renderers: ChildRenderer[], message: HistoryMessageAlme): VTree[]
    {
        return renderers.reduce((nodes, render) => [...nodes, render(message) as VTree], [] as VTree[]);
    }

    buildResponseProperties(message: HistoryMessageAlme): Dictionary
    {
        const properties: Dictionary = { className: this._settings.classNames.almeResponseClassName };

        if (this._settings.elementIds)
        {
            const { almeResponseIdNameBase, almeMessageDisplayNameIdBase } = this._settings.elementIds;
            const entryId = message.extensionData?.entryId;

            properties.id = entryId ? `${almeResponseIdNameBase}${entryId}` : null;
            properties.ariaLabelledBy = entryId ? `${almeMessageDisplayNameIdBase}${entryId}` : null;
        }

        return this._mutators.reduce((props, mutate) => mutate(props), properties);
    }

    buildMessageProperties(): Dictionary
    {
        const properties: Dictionary = { className: this._settings.classNames.almeResponseMessageClassName };

        if (this.shouldDisable() || this._settings.accessibility?.focusable?.almeResponseMessage) { properties.tabIndex = -1; }

        return properties;
    }

    shouldDisable(): boolean
    {
        const { Base, AlmeMessage } = this._settings.stateLocations.Window;

        return this._store?.getState()[Base][AlmeMessage.Base][AlmeMessage.shouldDisable];
    }

    //#endregion
}




// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/message-renderer-alme/src/renderers/renderer.ts